import { z } from "zod"
import { SuggestionChipSchema } from "./api.types"
import type { SuggestionChip, ToolCallBody } from "./api.types"

// Message sender types
export type MessageSender = "user" | "agent";

// Message types
export type MessageType = "text" | "widget" | "error" | "tool_call";

// Base message schema
export const BaseMessageSchema = z.object({
  id: z.string(),
  sender: z.enum(["user", "agent"]),
  timestamp: z.number(),
})

// Widget attached to a text message
export const AttachedWidgetSchema = z.object({
  widgetType: z.string(),
  data: z.any(),
})

// Text message schema
export const TextMessageSchema = BaseMessageSchema.extend({
  type: z.literal("text"),
  content: z.string(),
  serverId: z.string().optional(),
  wasHelpful: z.boolean().nullable().optional(),
  widgets: z.array(AttachedWidgetSchema).optional(),
  // Contextual follow-up chips, attached after the message via a `suggestions`
  // stream event (arrives a beat after `complete`).
  suggestions: z.array(SuggestionChipSchema).optional(),
})

// Error message schema
export const ErrorMessageSchema = BaseMessageSchema.extend({
  type: z.literal("error"),
  content: z.string(),
})

// Tool call message schema (shown when agent is calling a tool)
export const ToolCallMessageSchema = BaseMessageSchema.extend({
  type: z.literal("tool_call"),
  display: z.string(),
})

// Combined message schema
export const MessageSchema = z.discriminatedUnion("type", [
  TextMessageSchema,
  ErrorMessageSchema,
  ToolCallMessageSchema,
])

// Inferred types from schemas
export type AttachedWidget = z.infer<typeof AttachedWidgetSchema>;
export type TextMessage = z.infer<typeof TextMessageSchema>;
export type ErrorMessage = z.infer<typeof ErrorMessageSchema>;
export type ToolCallMessage = z.infer<typeof ToolCallMessageSchema>;
export type Message = z.infer<typeof MessageSchema>;

// Chat state with thread management
export interface ChatState {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  threadId: string | null;
}

// Chat actions
export type ChatAction =
  | { type: "ADD_MESSAGE"; payload: Message }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "CLEAR_MESSAGES" }
  | { type: "SET_THREAD_ID"; payload: string | null }
  | {
      type: "SET_MESSAGE_VOTE";
      payload: { id: string; wasHelpful: boolean | null };
    }
  | {
      type: "SET_MESSAGE_SUGGESTIONS";
      payload: { serverId: string; chips: SuggestionChip[] };
    };

// Utility function to create messages
export function createUserMessage(content: string): TextMessage {
  return {
    id: crypto.randomUUID(),
    sender: "user",
    type: "text",
    content,
    timestamp: Date.now(),
  }
}

export function createAgentTextMessage(
  content: string,
  serverId?: string,
  widgets?: AttachedWidget[],
): TextMessage {
  return {
    id: crypto.randomUUID(),
    sender: "agent",
    type: "text",
    content,
    timestamp: Date.now(),
    serverId,
    wasHelpful: undefined,
    widgets,
  }
}

export function createErrorMessage(content: string): ErrorMessage {
  return {
    id: crypto.randomUUID(),
    sender: "agent",
    type: "error",
    content,
    timestamp: Date.now(),
  }
}

export function createToolCallMessage(toolCall: ToolCallBody): ToolCallMessage {
  return {
    id: crypto.randomUUID(),
    sender: "agent",
    type: "tool_call",
    display: toolCall.display,
    timestamp: Date.now(),
  }
}
